import React, { useState } from "react";
import { Button, IconButton, SplitButton, TextInput, Textarea, OptionCard, Dropdown, ToggleGroup } from "components";
import { Stage, SearchIcon, PlusIcon, PlayIcon, GridIcon, ListIcon } from "./_shared";

const SplitButtonDemo = ({ variant }) => (
  <SplitButton variant={variant} startIcon={<PlayIcon />} onClick={() => {}} items={[{ label: "Edit game", onClick: () => {} }, { label: "Move back to draft", onClick: () => {} }, { label: "Archive", onClick: () => {}, destructive: true }]}>
    Launch Game
  </SplitButton>
);
const DropdownDemo = () => {
  const [v, setV] = useState("touched");
  return <Dropdown label="Sort:" value={v} onChange={setV} options={[{ value: "touched", label: "Last touched" }, { value: "edited", label: "Last edited" }, { value: "created", label: "Last created" }, { value: "name", label: "A–Z" }]} />;
};
const OptionCardDemo = () => {
  const [v, setV] = useState("medium");
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, width: 440 }}>
      {[["easy", "Easy", "Crowd-pleasers"], ["medium", "Medium", "A fair fight"], ["hard", "Hard", "For the know-it-alls"]].map(([id, name, desc]) => (
        <OptionCard key={id} name={name} description={desc} selected={v === id} onSelect={() => setV(id)} />
      ))}
    </div>
  );
};
const ToggleGroupDemo = () => {
  const [v, setV] = useState("grid");
  return <ToggleGroup value={v} onChange={setV} options={[{ value: "grid", icon: <GridIcon />, ariaLabel: "Grid" }, { value: "list", icon: <ListIcon />, ariaLabel: "List" }]} />;
};

export const formsEntries = [
  {
    id: "button", name: "Button", group: "Forms & buttons", status: "live",
    description: "The canonical action control, surface-aware. SplitButton, IconButton, EmptyState and ConfirmDialog all build on it.",
    props: [
      { name: "variant", type: "'primary'|'secondary'|'ghost'|'danger'|'cta'", default: "'primary'", description: "Style (cta = signature gradient + glow)" },
      { name: "size", type: "'sm'|'md'|'lg'", default: "'md'", description: "Size (md=38px, aligns with TextInput)" },
      { name: "startIcon / endIcon", type: "node", description: "Icons" },
      { name: "loading", type: "bool", default: "false", description: "Spinner + disabled" },
      { name: "as", type: "element", default: "'button'", description: "e.g. 'a' / RouterLink" },
    ],
    variants: [
      { label: "Variants", render: () => <Stage><div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}><Button variant="primary">Primary</Button><Button variant="secondary">Secondary</Button><Button variant="ghost">Ghost</Button><Button variant="danger">Danger</Button></div></Stage> },
      { label: "CTA (signature)", render: () => <Stage><Button variant="cta" startIcon={<PlayIcon />}>Launch Game</Button></Stage> },
      { label: "Sizes + icon", render: () => <Stage><div style={{ display: "flex", gap: 10, alignItems: "center" }}><Button size="sm">Small</Button><Button size="md" startIcon={<PlusIcon />}>Medium</Button><Button size="lg">Large</Button></div></Stage> },
      { label: "States", render: () => <Stage><div style={{ display: "flex", gap: 10, alignItems: "center" }}><Button loading>Loading</Button><Button disabled>Disabled</Button></div></Stage> },
      { label: "On game surface", render: () => <Stage surface="game"><div style={{ display: "flex", gap: 10 }}><Button variant="cta">Go Live</Button><Button variant="secondary">Secondary</Button></div></Stage> },
    ],
    code: `import { Button } from "components";

<Button variant="primary">Primary</Button>
<Button variant="secondary">Secondary</Button>
<Button variant="ghost">Ghost</Button>
<Button variant="danger">Danger</Button>
<Button variant="cta" startIcon={<PlayIcon />}>Launch Game</Button>
<Button size="sm">Small</Button>
<Button loading>Loading</Button>
<Button disabled>Disabled</Button>`,
  },
  {
    id: "split-button", name: "SplitButton", group: "Forms & buttons", status: "live",
    description: "Primary action + caret menu of secondary actions (PRD §5.4). Composes Button so the primary half is identical to a normal button.",
    props: [
      { name: "children", type: "node", description: "Primary label" },
      { name: "onClick", type: "fn", description: "Primary action" },
      { name: "items", type: "[{label,onClick,icon?,destructive?}]", description: "Menu" },
      { name: "variant", type: "'primary'|'secondary'|'ghost'|'danger'|'cta'", default: "'primary'", description: "Style" },
    ],
    variants: [
      { label: "Primary", render: () => <Stage><SplitButtonDemo variant="primary" /></Stage> },
      { label: "Secondary", render: () => <Stage><SplitButtonDemo variant="secondary" /></Stage> },
    ],
    code: `import { SplitButton } from "components";

<SplitButton
  variant="primary"
  startIcon={<PlayIcon />}
  onClick={launch}
  items={[
    { label: "Edit game", onClick: edit },
    { label: "Move back to draft", onClick: toDraft },
    { label: "Archive", onClick: archive, destructive: true },
  ]}
>
  Launch Game
</SplitButton>`,
  },
  {
    id: "icon-button", name: "IconButton", group: "Forms & buttons", status: "live",
    description: "Square, icon-only button sharing Button's variants and sizing so it sits flush beside a Button. Requires an accessible label.",
    props: [
      { name: "variant", type: "'primary'|'secondary'|'ghost'|'danger'", default: "'ghost'", description: "Style" },
      { name: "size", type: "'sm'|'md'|'lg'", default: "'md'", description: "Size" },
      { name: "label", type: "string", description: "aria-label (required)" },
    ],
    variants: [
      { label: "Variants + sizes", render: () => <Stage><div style={{ display: "flex", gap: 10, alignItems: "center" }}><IconButton variant="ghost" label="Search"><SearchIcon /></IconButton><IconButton variant="secondary" label="Add"><PlusIcon /></IconButton><IconButton variant="primary" label="Play"><PlayIcon /></IconButton><IconButton variant="ghost" size="sm" label="Search small"><SearchIcon /></IconButton></div></Stage> },
    ],
    code: `import { IconButton } from "components";

<IconButton label="Search"><SearchIcon /></IconButton>
<IconButton variant="secondary" label="Add"><PlusIcon /></IconButton>
<IconButton variant="primary" label="Play"><PlayIcon /></IconButton>`,
  },
  {
    id: "text-input", name: "TextInput", group: "Forms & buttons", status: "live",
    description: "Brand text field, surface-aware (reads --field-* vars). Generalizes the question flow's AnswerTextField.",
    props: [
      { name: "label", type: "string", description: "Label above" },
      { name: "leftIcon", type: "node", description: "Inline icon" },
      { name: "error", type: "bool", default: "false", description: "Error styling" },
      { name: "helperText", type: "string", description: "Caption" },
    ],
    variants: [
      { label: "Manager", render: () => <Stage width={260}><TextInput label="Game name" placeholder="Friday Trivia Night" /></Stage> },
      { label: "Game", render: () => <Stage surface="game" width={260}><TextInput label="Your answer" placeholder="What is…" /></Stage> },
      { label: "Icon", render: () => <Stage width={260}><TextInput leftIcon={<SearchIcon />} placeholder="Search games…" /></Stage> },
      { label: "Error", render: () => <Stage width={260}><TextInput label="Wager" error helperText="Cannot exceed your score" defaultValue="9999" /></Stage> },
    ],
    notes: ["AnswerTextField still backs the question flow; convergence is a follow-up."],
    code: `import { TextInput } from "components";

<TextInput label="Game name" placeholder="Friday Trivia Night" />
<TextInput leftIcon={<SearchIcon />} placeholder="Search games…" />
<TextInput label="Wager" error helperText="Cannot exceed your score" />`,
  },
  {
    id: "textarea", name: "Textarea", group: "Forms & buttons", status: "live",
    description: "Multiline counterpart to TextInput — same field chrome (gx-field), auto-height, vertically resizable.",
    props: [
      { name: "label", type: "string|node", description: "Label above" },
      { name: "helperText", type: "string", description: "Caption" },
      { name: "rows", type: "number", default: "4", description: "Initial height" },
      { name: "error", type: "bool", default: "false", description: "Error styling" },
    ],
    variants: [
      { label: "Manager", render: () => <Stage width={320}><Textarea label="Game description" rows={4} placeholder="A fun movie trivia night…" /></Stage> },
      { label: "Game", render: () => <Stage surface="game" width={320}><Textarea label="Notes" rows={3} placeholder="Type here…" /></Stage> },
    ],
    code: `import { Textarea } from "components";

<Textarea label="Game description" rows={4} placeholder="A fun movie trivia night…" />`,
  },
  {
    id: "option-card", name: "OptionCard", group: "Forms & buttons", status: "live",
    description: "Selectable card — brand replacement for radio buttons. Renders as a button; supports icon, meta, description, detail lines, and example. Used by the create flow's difficulty & length pickers.",
    props: [
      { name: "selected / onSelect", type: "bool / fn", description: "Controlled selection" },
      { name: "icon / iconColor", type: "node / css", description: "Optional leading icon" },
      { name: "meta", type: "string", description: "Large emphasized text (e.g. '~15 min')" },
      { name: "name / description", type: "string", description: "Title + supporting line" },
      { name: "detailLines", type: "string[]", description: "Small stacked rows" },
      { name: "layout", type: "'stack'|'row'", default: "'stack'", description: "Orientation" },
    ],
    variants: [
      { label: "Difficulty", render: () => <Stage><OptionCardDemo /></Stage> },
      { label: "Length", render: () => <Stage width={200}><OptionCard meta="~30 min" name="Standard" detailLines={["2 rounds + Final", "5 categories"]} selected onSelect={() => {}} /></Stage> },
    ],
    code: `import { OptionCard } from "components";

<OptionCard name="Medium" description="A fair fight" selected onSelect={pick} />
<OptionCard meta="~30 min" name="Standard" detailLines={["2 rounds + Final", "5 categories"]} />`,
  },
  {
    id: "dropdown", name: "Dropdown", group: "Forms & buttons", status: "live",
    description: "Select-style dropdown (the sort control). Brand secondary trigger + the shared brand menu.",
    props: [
      { name: "value / options / onChange", type: "—", description: "Controlled select" },
      { name: "label", type: "string", description: "Muted prefix (e.g. 'Sort:')" },
    ],
    variants: [{ label: "Sort control", render: () => <Stage><DropdownDemo /></Stage> }],
    code: `import { Dropdown } from "components";

const [sort, setSort] = useState("touched");

<Dropdown
  label="Sort:"
  value={sort}
  onChange={setSort}
  options={[
    { value: "touched", label: "Last touched" },
    { value: "edited", label: "Last edited" },
    { value: "created", label: "Last created" },
    { value: "name", label: "A–Z" },
  ]}
/>`,
  },
  {
    id: "toggle-group", name: "ToggleGroup", group: "Forms & buttons", status: "live",
    description: "Segmented control for 2+ exclusive options (grid / list view).",
    props: [{ name: "options / value / onChange", type: "—", description: "Controlled" }],
    variants: [{ label: "Grid / list", render: () => <Stage><ToggleGroupDemo /></Stage> }],
    code: `import { ToggleGroup } from "components";

const [view, setView] = useState("grid");

<ToggleGroup
  value={view}
  onChange={setView}
  options={[
    { value: "grid", icon: <GridIcon />, ariaLabel: "Grid" },
    { value: "list", icon: <ListIcon />, ariaLabel: "List" },
  ]}
/>`,
  },
];
